import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import {
  apiErrorText,
  useAnalyzeRateCheckMutation,
  useGetRateCheckQuery,
  useGetRateCheckStatusQuery,
  useSetRateCheckPriceZoneMutation,
} from '@shared/api';
import { PriceZoneField, zoneLabel } from './PriceZoneField';
import type { AppliedRate, RateCheckEstimate } from '@shared/api';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { IconArrowLeft } from '@shared/ui/icons';
import { RateCheckTerritory } from './TerritoryField';
import { useAgentPermission } from '@entities/user';
import { ParseWarningsBanner } from './ParseWarningsBanner';
import { StatusReadError } from './StatusReadError';
import {
  canAnalyze,
  rateCheckPollInterval,
  rateCheckStatusView,
  rerunConfirmText,
} from './rateCheckStatus';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  width: max-content;

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Meta = styled.div<{ $mb?: number }>`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
  ${({ $mb }) => ($mb !== undefined ? `margin-bottom: ${$mb}px;` : '')}
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};
`;

const MetaLabel = styled.div`
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const MetaValue = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Tab = styled.button<{ $active?: boolean }>`
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primarySoft : 'transparent'};
  color: inherit;
  font: inherit;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
`;

const Section = styled.div`
  font-weight: 600;
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  th {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.secondary};
    background: ${({ theme }) => theme.colors.surface};
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  td.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  td.code {
    font-family: ${({ theme }) => theme.font.mono};
    font-size: 11.5px;
    white-space: nowrap;
  }
`;

const Note = styled.div<{ $mt?: number; $flush?: boolean; $danger?: boolean }>`
  padding: ${({ theme, $flush }) => ($flush ? '0' : theme.spacing(3))};
  font-size: 13px;
  color: ${({ theme, $danger }) =>
    $danger ? theme.colors.status.danger : theme.colors.text.muted};
  ${({ $mt }) => ($mt !== undefined ? `margin-top: ${$mt}px;` : '')}
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const ActionsRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const DiscrepanciesLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    background: ${({ theme }) => theme.colors.surface};
  }
`;

function fmtNum(n: number | null | undefined): string {
  if (typeof n !== 'number') return '—';
  // Группируем тысячи пробелом, два знака после запятой по сметным стандартам.
  return n.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}

function fmtMoney(n: number | null | undefined): string {
  if (typeof n !== 'number') return '—';
  return n.toLocaleString('ru-RU', {
    maximumFractionDigits: 0,
  });
}

function groupBySection(positions: AppliedRate[]): Array<{ section: string | null; rows: AppliedRate[] }> {
  // Сохраняем порядок появления разделов в исходной смете (стабильный sort).
  const order: string[] = [];
  const map = new Map<string, AppliedRate[]>();
  for (const p of positions) {
    const key = p.section ?? '__no_section__';
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key)!.push(p);
  }
  return order.map((key) => ({
    section: key === '__no_section__' ? null : key,
    rows: map.get(key)!,
  }));
}

interface EstimateBlock {
  estimate: RateCheckEstimate;
  positions: AppliedRate[];
}

function EstimatePanel({ block }: { block: EstimateBlock }) {
  const { estimate, positions } = block;
  const groups = groupBySection(positions);
  return (
    <Card>
      <Meta $mb={16}>
        <MetaItem>
          <MetaLabel>Лист</MetaLabel>
          <MetaValue>{estimate.sheet_name}</MetaValue>
        </MetaItem>
        {estimate.object_name && (
          <MetaItem>
            <MetaLabel>Объект</MetaLabel>
            <MetaValue>{estimate.object_name}</MetaValue>
          </MetaItem>
        )}
        {estimate.smeta_code && (
          <MetaItem>
            <MetaLabel>Шифр сметы</MetaLabel>
            <MetaValue>{estimate.smeta_code}</MetaValue>
          </MetaItem>
        )}
        {typeof estimate.base_total === 'number' && (
          <MetaItem>
            <MetaLabel>Базисная стоимость, ₽</MetaLabel>
            <MetaValue>{fmtMoney(estimate.base_total)}</MetaValue>
          </MetaItem>
        )}
        <MetaItem>
          <MetaLabel>Позиций</MetaLabel>
          <MetaValue>{positions.length}</MetaValue>
        </MetaItem>
      </Meta>
      <Table>
        <thead>
          <tr>
            <th>№</th>
            <th>Шифр</th>
            <th>Наименование</th>
            <th>Ед.</th>
            <th>Кол-во</th>
            <th>ПЗ</th>
            <th>ОЗП</th>
            <th>ЭМ</th>
            <th>ЗПМ</th>
            <th>Материалы</th>
            <th>НР %</th>
            <th>СП %</th>
          </tr>
        </thead>
        <tbody>
          {groups.flatMap((g) => {
            const sectionKey = g.section ?? `nosection-${groups.indexOf(g)}`;
            const headerRow = g.section ? (
              <tr key={`s-${sectionKey}`}>
                <td colSpan={12}>
                  <Section>{g.section}</Section>
                </td>
              </tr>
            ) : null;
            const dataRows = g.rows.map((p) => (
              <tr key={p.id}>
                <td>{p.position_no ?? ''}</td>
                <td className="code">{p.code}</td>
                <td>{p.name}</td>
                <td>{p.unit ?? ''}</td>
                <td className="num">{fmtNum(p.qty)}</td>
                <td className="num">{fmtNum(p.unit_pz)}</td>
                <td className="num">{fmtNum(p.unit_ozp)}</td>
                <td className="num">{fmtNum(p.unit_em)}</td>
                <td className="num">{fmtNum(p.unit_zpm)}</td>
                <td className="num">{fmtNum(p.unit_mat)}</td>
                <td className="num">{fmtNum(p.applied_nr_pct)}</td>
                <td className="num">{fmtNum(p.applied_sp_pct)}</td>
              </tr>
            ));
            return headerRow ? [headerRow, ...dataRows] : dataRows;
          })}
        </tbody>
      </Table>
    </Card>
  );
}

export function RateCheckDetail() {
  const { id } = useParams<{ id: string }>();
  const { can_write } = useAgentPermission('smeta');
  // Позиции смет не меняются от анализа — карточка читается один раз, а
  // опрашивается лёгкий статус и только пока идёт работа.
  const { data, isLoading, isError } = useGetRateCheckQuery(id!, { skip: !id });
  const [polling, setPolling] = useState(0);
  const statusQ = useGetRateCheckStatusQuery(id!, {
    skip: !id,
    pollingInterval: polling,
    skipPollingIfUnfocused: true,
    refetchOnFocus: true,
  });
  const liveStatus = statusQ.data?.check.status;
  useEffect(() => setPolling(rateCheckPollInterval(liveStatus)), [liveStatus]);
  const [analyze, { isLoading: starting }] = useAnalyzeRateCheckMutation();
  const [setPriceZone] = useSetRateCheckPriceZoneMutation();
  const [activeSheet, setActiveSheet] = useState<number>(0);

  if (isLoading)
    return (
      <Card>
        <Note>Загрузка…</Note>
      </Card>
    );
  if (isError || !data)
    return (
      <Card>
        <Note>Не удалось загрузить детали проверки.</Note>
      </Card>
    );

  const blocks = data.estimates;
  const active = blocks[activeSheet];
  // Статус и причина сбоя — из опроса статуса, он свежее карточки.
  const check = statusQ.data?.check ?? data.check;
  const status = check.status;
  const statusView = rateCheckStatusView(status);
  const annotated = statusQ.data?.annotated ?? 0;
  // Находки прошлого анализа на месте и при сбое повторного (A07).
  const hasFindings = !!statusQ.data?.revision;

  async function onAnalyze() {
    if (!id) return;
    // Число размеченных находок — на момент нажатия: их могли разметить на
    // странице расхождений или в другой вкладке.
    let marked = annotated;
    try {
      marked = (await statusQ.refetch().unwrap()).annotated;
    } catch {
      // Статус не перечитался — спрашиваем по последнему известному числу.
    }
    if (marked > 0 && !window.confirm(rerunConfirmText(marked))) return;
    try {
      await analyze(id).unwrap();
    } catch (err) {
      window.alert(apiErrorText(err, 'Не удалось запустить анализ.'));
    }
  }

  return (
    <Wrap>
      <Back to="/agents/smeta/rate-checks">
        <IconArrowLeft size={16} />
        <span>К списку загрузок</span>
      </Back>

      <ParseWarningsBanner warnings={data.warnings} />

      <Card>
        <Meta>
          <MetaItem>
            <MetaLabel>Файл</MetaLabel>
            <MetaValue>{data.check.source_filename}</MetaValue>
          </MetaItem>
          <MetaItem>
            <MetaLabel>Категория местности</MetaLabel>
            <MetaValue>
              {/* Категории — из опроса статуса, как и сам статус: карточку
                  с позициями конец анализа не перечитывает, и категория
                  результата в ней осталась бы от прошлого анализа. */}
              <RateCheckTerritory
                key={data.check.id}
                checkId={data.check.id}
                value={check.territory_category}
                resultValue={check.analyzed_territory_category}
                status={status}
                canEdit={can_write}
              />
            </MetaValue>
          </MetaItem>
          <MetaItem>
            <MetaLabel>Статус</MetaLabel>
            <MetaValue>
              <Badge $tone={statusView.tone}>{statusView.label}</Badge>
            </MetaValue>
          </MetaItem>
          <MetaItem>
            <MetaLabel>ЛСР</MetaLabel>
            <MetaValue>{check.sheet_count}</MetaValue>
          </MetaItem>
          <MetaItem>
            <MetaLabel>Позиций всего</MetaLabel>
            <MetaValue>{check.position_count}</MetaValue>
          </MetaItem>
          <MetaItem>
            <MetaLabel>Регион</MetaLabel>
            <MetaValue>
              {data.check.region ?? 'не указан'}
              {data.check.price_zone ? `, ${zoneLabel(data.check.price_zone)}` : ''}
            </MetaValue>
          </MetaItem>
        </Meta>
        <PriceZoneField
          key={data.check.id}
          owner={data.check.id}
          region={data.check.region}
          zone={data.check.price_zone}
          canWrite={can_write}
          onSave={(zone) => setPriceZone({ id: data.check.id, price_zone: zone }).unwrap()}
        />
        {check.error_message && <Note $mt={12}>{check.error_message}</Note>}
        {statusQ.isError && (
          <StatusReadError onRetry={() => void statusQ.refetch()} retrying={statusQ.isFetching} />
        )}
        <ActionBar>
          <div>
            {status === 'analyzing' && (
              <Note $flush>
                Анализ идёт — статус на странице обновится сам, когда он закончится.
                {hasFindings && ' До тех пор доступны находки прошлого анализа.'}
              </Note>
            )}
            {status === 'analyze_failed' && (
              <Note $flush $danger>
                Последний запуск анализа завершился ошибкой. Можно запустить ещё раз.
                {hasFindings && ' Находки и решения прошлого успешного анализа сохранены.'}
              </Note>
            )}
            {status === 'pending' && (
              <Note $flush>Файл ещё не разобран — дождитесь окончания разбора.</Note>
            )}
          </div>
          <ActionsRight>
            {(status === 'analyzed' || hasFindings) && (
              <DiscrepanciesLink to={`/agents/smeta/rate-checks/${id}/discrepancies`}>
                {status === 'analyzed' ? 'Перейти к расхождениям →' : 'Находки прошлого анализа →'}
              </DiscrepanciesLink>
            )}
            <Button
              type="button"
              onClick={onAnalyze}
              disabled={!can_write || starting || !canAnalyze(status)}
            >
              {starting
                ? 'Ставлю задачу…'
                : status === 'analyzed' || hasFindings
                  ? 'Запустить ещё раз'
                  : 'Запустить анализ'}
            </Button>
          </ActionsRight>
        </ActionBar>
      </Card>

      {blocks.length > 1 && (
        <Tabs>
          {blocks.map((b, i) => (
            <Tab
              key={b.estimate.id}
              type="button"
              $active={i === activeSheet}
              onClick={() => setActiveSheet(i)}
            >
              ЛСР {b.estimate.sheet_name}
              {b.estimate.object_name ? ` · ${b.estimate.object_name.slice(0, 28)}` : ''}
            </Tab>
          ))}
        </Tabs>
      )}

      {active && <EstimatePanel block={active} />}
      {blocks.length === 0 && (
        <Card>
          <Note>В файле не найдено ни одной ЛСР с распознаваемой шапкой.</Note>
        </Card>
      )}
    </Wrap>
  );
}
